// Shell: session, navigation, theme and hash routing.
import { api, ApiError, setCsrf } from "./api.ts";
import { BRAND, icon } from "./icons.ts";
import { esc, initials } from "./ui.ts";
import type { Health, Me } from "./types.ts";

type View = { mount: (main: HTMLElement, ctx: ViewContext) => void | Promise<void> };
export type ViewContext = { route: Route; me: Me; health: Health | null; refreshNav: () => Promise<void> };
export type Route = { view: string; parts: string[]; query: URLSearchParams };

const views: Record<string, () => Promise<View>> = {
  login: () => import("./views/login.ts"),
  chat: () => import("./views/chat.ts"),
  activity: () => import("./views/activity.ts"),
  admin: () => import("./views/admin.ts"),
  settings: () => import("./views/settings.ts"),
};
const TITLES: Record<string, string> = { chat: "Chat", activity: "Actividad", admin: "Administración", settings: "Ajustes" };

const media = matchMedia("(prefers-color-scheme: dark)");
type ThemePreference = "system" | "light" | "dark";
export const theme: { preference: () => ThemePreference; apply: (pref?: ThemePreference) => void } = {
  preference: (): ThemePreference => { try { return (localStorage.getItem("theme") as "light" | "dark") || "system"; } catch { return "system"; } },
  apply(pref: ThemePreference = theme.preference()) {
    try { if (pref === "system") localStorage.removeItem("theme"); else localStorage.setItem("theme", pref); } catch { /* storage blocked */ }
    document.documentElement.dataset.theme = pref === "dark" || (pref === "system" && media.matches) ? "dark" : "light";
  },
};
media.addEventListener("change", () => theme.apply());

export function route(): Route {
  const [path = "", q = ""] = location.hash.replace(/^#\/?/, "").split("?");
  const parts = path.split("/").filter(Boolean);
  return { view: parts[0] || "chat", parts, query: new URLSearchParams(q) };
}
export const go = (hash: string) => { location.hash = hash; };

let me: Me | null = null;
let health: Health | null = null;
export const signedOut = () => { me = null; setCsrf(""); };
export const signedIn = (m: Me) => { me = m; setCsrf(m.csrfToken); };

async function pendingCount(): Promise<number> {
  try { return (await api.confirmations("pending")).items.length; } catch { return 0; }
}

const nav = (view: string, pending: number) => [
  ["chat", "message-square", "Chat", 0],
  ["activity", "activity", "Actividad", pending],
  ...(me?.user.role === "admin" ? [["admin", "users", "Administración", 0]] : []),
  ["settings", "settings", "Ajustes", 0],
].map(([v, ico, label, n]) => `<a href="#/${v}" ${v === view ? 'aria-current="page"' : ""}>${icon(String(ico))}<span>${label}</span>${n ? `<span class="badge" aria-label="${n} pendientes">${n}</span>` : ""}</a>`).join("");

async function refreshNav() {
  const r = route();
  const n = await pendingCount();
  document.querySelectorAll(".sidebar nav, .tabbar").forEach((el) => { el.innerHTML = nav(r.view, n); });
}

let renderToken = 0;
async function render() {
  const token = ++renderToken;
  const r = route();
  const root = document.getElementById("root")!;
  health ??= await api.health().catch(() => null);
  if (!me && r.view !== "login") {
    try { signedIn(await api.me()); } catch (err) {
      if (err instanceof ApiError && err.problem.status === 401) return go("#/login");
      root.innerHTML = `<main class="login" id="main"><div class="login-card"><div class="failure" role="alert">${icon("circle-alert")}<h2>No se puede conectar</h2><p>${esc((err as ApiError).problem?.detail ?? "Vuelve a intentarlo.")}</p><button class="btn" onclick="location.reload()">Reintentar</button></div></div></main>`;
      return;
    }
  }
  if (r.view === "login") {
    if (me) return go("#/chat");
    root.innerHTML = "";
    (await views.login!()).mount(root, { route: r, me: null as never, health, refreshNav });
    document.title = "Entrar · Vera";
    return;
  }
  if (!views[r.view] || (r.view === "admin" && me!.user.role !== "admin")) return go("#/chat");
  const pending = await pendingCount();
  if (token !== renderToken) return;
  const name = me!.user.name;
  root.innerHTML = `
    <div class="app">
      <aside class="sidebar" aria-label="Navegación">
        <a class="brand" href="#/chat">${BRAND}<span>Vera</span></a>
        <nav>${nav(r.view, pending)}</nav>
        <div class="sidebar-footer">
          <div class="person"><span class="avatar" aria-hidden="true">${esc(initials(name))}</span>
            <span class="info"><strong>${esc(name)}</strong><span>${esc(me!.user.email)}</span></span></div>
        </div>
      </aside>
      <header class="topbar">
        <a class="brand" href="#/chat" aria-label="Vera, inicio">${BRAND}</a>
        <h1>${TITLES[r.view]}</h1>
        <span class="demo"><button type="button" title="Demostración: datos ficticios; pagos y correos simulados" aria-label="Demostración con datos ficticios">Demo</button></span>
      </header>
      <main class="main" id="main" tabindex="-1"></main>
      <nav class="tabbar" aria-label="Navegación">${nav(r.view, pending)}</nav>
    </div>`;
  document.title = `${TITLES[r.view]} · Vera`;
  if (me!.user.panic) {
    document.querySelector(".topbar h1")!.insertAdjacentHTML("afterend", `<span class="chip blocked">${icon("octagon-alert")} Bloqueado</span>`);
  }
  await (await views[r.view]!()).mount(document.getElementById("main")!, { route: r, me: me!, health, refreshNav });
}

let previousView = "";
window.addEventListener("hashchange", () => {
  const v = route().view;
  void render().then(() => {
    // Move focus to the new view for screen readers, unless the user is already working inside it.
    const active = document.activeElement;
    const userBusy = active && active !== document.body && document.contains(active) && document.getElementById("main")?.contains(active);
    if (v !== previousView && !userBusy) document.getElementById("main")?.focus({ preventScroll: true });
    previousView = v;
  });
});
theme.apply();
// Shared from another app (Android share sheet, e.g. a WhatsApp message): keep it for the chat and clean the address.
const shared = new URLSearchParams(location.search);
if (shared.has("text") || shared.has("title") || shared.has("url")) {
  sessionStorage.setItem("vera.share", ["title", "text", "url"].map((k) => shared.get(k)).filter(Boolean).join("\n"));
  history.replaceState(null, "", "/#/chat");
}
void render().then(() => { previousView = route().view; });
if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => { /* works without it */ });
